import { Button, Chip, Dialog, DialogContent, DialogTitle } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";
import { z } from "zod";

import { useAuth } from "../auth/AuthProvider";
import { DataTable, ErrorState, PageHeader, PermissionGate, useListParams } from "../components";
import { applyServerErrors, FormSection, FormSelect, FormShell, FormText, useAppForm } from "../components/Form";
import help from "./HELP.md?raw";
import { useDeleteSite, useSaveSite, useSites, type Site } from "./useData";

const schema = z.object({
  code: z.string().min(1).max(20).transform((s) => s.toUpperCase()),
  name: z.string().min(1).max(200),
  kind: z.enum(["plant", "division", "office", "warehouse"]),
  timezone: z.string(),
  is_active: z.enum(["true", "false"]),
  is_default: z.enum(["true", "false"]),
});
type Values = z.infer<typeof schema>;

function SiteDialog({ site, onClose }: { site: Partial<Site> | null; onClose: () => void }) {
  const save = useSaveSite();
  const [error, setError] = useState<string | null>(null);
  const form = useAppForm(schema, { defaultValues: { code: site?.code ?? "", name: site?.name ?? "", kind: (site?.kind as Values["kind"]) ?? "plant", timezone: site?.timezone ?? "", is_active: site?.is_active === false ? "false" : "true", is_default: site?.is_default ? "true" : "false" } });
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{site?.id ? `Edit ${site.code}` : "New site"}</DialogTitle>
      <DialogContent>
        <FormShell form={form} error={error} busy={save.isPending} onSubmit={async (v) => {
          try { await save.mutateAsync({ id: site?.id, ...v, is_active: v.is_active === "true", is_default: v.is_default === "true" }); onClose(); }
          catch (err) { setError(applyServerErrors(form, err)); }
        }} actions={<Button onClick={onClose}>Cancel</Button>}>
          <FormSection>
            <FormText name="code" label="Code" />
            <FormText name="name" label="Name" />
            <FormSelect name="kind" label="Type" options={["plant", "division", "office", "warehouse"].map((k) => ({ value: k, label: k }))} />
            <FormText name="timezone" label="Timezone" helperText="Blank = organization default" />
            <FormSelect name="is_active" label="Status" options={[{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }]} />
            <FormSelect name="is_default" label="Default site" options={[{ value: "false", label: "No" }, { value: "true", label: "Yes" }]} />
          </FormSection>
        </FormShell>
      </DialogContent>
    </Dialog>
  );
}

export function Sites() {
  const { hasPermission } = useAuth();
  const [params, setParams] = useListParams({ ordering: "code" });
  const sites = useSites(params);
  const remove = useDeleteSite();
  const [editing, setEditing] = useState<Partial<Site> | null>(null);
  const columns: GridColDef<Site>[] = [
    { field: "code", headerName: "Code", width: 110 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "kind", headerName: "Type", width: 120 },
    { field: "timezone", headerName: "Timezone", width: 180 },
    { field: "is_default", headerName: "Default", width: 90, renderCell: (p) => p.value ? <Chip label="default" size="small" color="primary" /> : null },
    { field: "is_active", headerName: "Active", width: 90, type: "boolean" },
    { field: "actions", headerName: "", width: 150, sortable: false, renderCell: (p) => hasPermission("accounts.site.change") ? (
      <><Button size="small" onClick={() => setEditing(p.row)}>Edit</Button>
        <PermissionGate permission="accounts.site.delete"><Button size="small" color="error" disabled={p.row.is_default} onClick={() => { if (confirm(`Delete site ${p.row.code}?`)) remove.mutate(p.row.id); }}>Delete</Button></PermissionGate></>
    ) : null },
  ];
  return (
    <>
      <PageHeader help={help} title="Sites" subtitle="Plants, divisions, offices and warehouses" crumbs={[{ label: "Settings" }, { label: "Sites" }]}
        actions={<PermissionGate permission="accounts.site.create"><Button variant="contained" onClick={() => setEditing({})}>New site</Button></PermissionGate>} />
      {remove.isError && <ErrorState message={remove.error.message} />}
      <DataTable viewKey="accounts.site.list" columns={columns} data={sites.data} isLoading={sites.isLoading} params={params} onParamsChange={setParams} />
      {editing && <SiteDialog site={editing} onClose={() => setEditing(null)} />}
    </>
  );
}
